import { wait } from './timers';

type PianoCustomVariables = {
  [name: string]: string;
};

type PianoParams = {
  activeMeters?: string;
  customVariables?: string;
  displayMode: string;
  iframeId: string;
  url: string;
};

type ParsedPianoParams = {
  activeMeters?: PianoMeterMap;
  customVariables?: PianoCustomVariables;
  displayMode: string;
  iframeId: string;
  url: string;
  // ...
};

type PianoParamRequirements = {
  activeMeters?: boolean;
  customVariables?: boolean;
};

type PianoWindow = Window & {
  TPParam?: {
    params: Partial<PianoParams>;
  };
};

type PianoMeter = {
  maxViews: number;
  meterName: string;
  totalViews: number;
  views: number;
  viewsLeft: number;
};

type PianoMeterMap = {
  [meterName: string]: PianoMeter;
};

type RetryOptions = {
  delayBetweenAttempts?: number;
  maxAttempts?: number;
};

export const getActiveMeters = async ({
  retry,
}: {
  retry?: RetryOptions;
} = {}): Promise<PianoMeterMap> => {
  const params = await waitForParams({
    requirements: { activeMeters: true },
    retry,
  });

  return params.activeMeters!;
};

export const getCurrentParams = (): Partial<PianoParams> => {
  const { TPParam } = <PianoWindow>window;
  if (!TPParam) {
    throw new Error('window.TPParam is not defined');
  }

  return TPParam.params;
};

export const getCustomVariables = async ({
  retry,
}: {
  retry?: RetryOptions;
} = {}): Promise<PianoCustomVariables> => {
  const params = await waitForParams({
    requirements: {
      customVariables: true,
    },
    retry,
  });

  return params.customVariables!;
};

export const getPianoParams = (): Partial<PianoParams> => {
  console.debug(
    `FLMPiano.getPianoParams() has been deprecated in favor of the async FLMPiano.waitForParams() or immediate FLMPiano.getCurrentParams()`,
  );

  return getCurrentParams();
};

const isCompleteParams = (
  params: Partial<PianoParams>,
): params is PianoParams =>
  typeof params.displayMode === 'string' &&
  typeof params.iframeId === 'string' &&
  typeof params.url === 'string';

const parseCustomVariables = (serializedData: string) =>
  JSON.parse(serializedData);

const parseMeters = (serializedData: string): PianoMeterMap => {
  const meters: PianoMeter[] = JSON.parse(serializedData);

  const meterMap: PianoMeterMap = {};
  for (const meter of meters) {
    meterMap[meter.meterName] = meter;
  }

  return meterMap;
};

export const waitForParams = async ({
  requirements: {
    activeMeters: requireActiveMeters = false,
    customVariables: requireCustomVariables = false,
  } = {},
  retry: { delayBetweenAttempts = 100, maxAttempts = 100 } = {},
}: {
  requirements?: PianoParamRequirements;
  retry?: RetryOptions;
} = {}): Promise<ParsedPianoParams> => {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    if (attempt > 1) {
      await wait(delayBetweenAttempts);
    }

    const params = getCurrentParams();
    if (!isCompleteParams(params)) {
      continue;
    }

    if (requireActiveMeters && typeof params.activeMeters !== 'string') {
      continue;
    }

    if (requireCustomVariables && typeof params.customVariables !== 'string') {
      continue;
    }

    return {
      ...params,
      activeMeters: requireActiveMeters
        ? parseMeters(params.activeMeters!)
        : undefined,
      customVariables: requireCustomVariables
        ? parseCustomVariables(params.customVariables!)
        : undefined,
    };
  }

  throw new Error(`Unable retrieve Piano params after ${maxAttempts} attempts`);
};
